import {useInfiniteQuery, useQuery} from '@tanstack/react-query';
import React, {ReactElement, useCallback, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import ReSelect, {MenuPlacement, MultiValue, SingleValue} from 'react-select';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {IElementQuestionAnswer, IQuestionAnswerType} from '../../../../../incase-domain/elements/base/questionElementBase';
import {IElementMultiSelect} from '../../../../../incase-domain/elements/question/multiSelect';
import {ElementInteraction, QuestionElementInteractions} from '../../../../../incase-domain/proptypes/runfile';
import {fetchCodebookEntries, fetchCodebookEntriesByIds} from '../../../../../service/codebook.service';
import {setPreventClickToNextSlide} from '../../../../../store/actions/presentation.actions';
import {loadedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {getSymmetricalDifference} from '../../../../../util/array';
import {getNextPage} from '../../../../../util/pagination';
import usePresScale from '../../../../../util/usePresScale';
import QuestionLayout from '../common/QuestionLayout';
import {mapCodebookEntriesToAnswers} from '../utils/codebook.utils';
import {handleSelectAnswer} from '../utils/handleTryButton.question.utils';
import MultiSelectOption from './MultiSelectOption';
import MultiSelectValueContainer from './MultiSelectValue';
import useMultiselectTry from './useMultiselectTry';
import BottomSheet from '../../../../../ui/components/common/BottomSheet';
import {usePlayerLabels} from '../../../../../hooks/usePlayerLabels';

interface MultiselectQuestionElementProps {
    element: IElementMultiSelect;
    savePresentationRunFile: (elementRunFile: ElementInteraction, newPoints?: number) => void;
    elementInteractions: QuestionElementInteractions;
    device?: DeviceType;
}

export interface IOptions {
    label: string;
    value: string;
    type: IQuestionAnswerType;
    comment?: string;
    parentKey?: string;
    depth?: number;
    numberOfChildren?: number;
}

export type MultiQuestionSelectProps = {
    answered?: boolean;
    correctColor?: string;
    incorrectColor?: string;
    debatableColor?: string;
    singleCorrect?: boolean;
    device?: DeviceType;
    onCommentOpen?: (answer: string, comment: string) => void;
};

const MENU_LIST_MAX_HEIGHT_EM = 15;
const MENU_VERTICAL_PADDING_EM = 1.5;

function getMenuFontSizePx(device: DeviceType, scale: number): number {
    return 16 * (device === DeviceType.DESKTOP ? scale : 1);
}

function getMenuEstimatedHeightPx(device: DeviceType, scale: number): number {
    const menuFontSizePx = getMenuFontSizePx(device, scale);
    return (MENU_LIST_MAX_HEIGHT_EM + MENU_VERTICAL_PADDING_EM) * menuFontSizePx;
}

function getMenuPlacementForSlide(selectEl: HTMLElement, menuEstimatedHeightPx: number): MenuPlacement {
    const rect = selectEl.getBoundingClientRect();
    const slideEl =
        selectEl.closest('#canvas-holder') ?? selectEl.closest('#modal-slide-holder') ?? selectEl.closest('.p-player__mobile_body');

    if (!(slideEl instanceof HTMLElement)) {
        return 'bottom';
    }

    const slideRect = slideEl.getBoundingClientRect();
    const spaceAbove = rect.top - slideRect.top;
    const spaceBelow = slideRect.bottom - rect.bottom;
    const shouldOpenTop = menuEstimatedHeightPx > spaceBelow && spaceAbove > spaceBelow;

    return shouldOpenTop ? 'top' : 'bottom';
}

export function MultiselectQuestionElement(props: MultiselectQuestionElementProps): ReactElement {
    const {element, savePresentationRunFile, elementInteractions, device = DeviceType.DESKTOP} = props;
    const {codebookId} = element;
    const {t} = useTranslation();
    const getLabel = usePlayerLabels();
    const closedPlaceholder = getLabel('elements-multiselect-select') || t('slide.elements.multiselectQuestion.selectPlaceholder');
    const openPlaceholder = getLabel('elements-multiselect-start-typing') || t('slide.elements.multiselectQuestion.startTyping');

    const [placeholder, setPlaceholder] = useState(closedPlaceholder);
    const [commentSheet, setCommentSheet] = useState<{answer: string; comment: string} | null>(null);
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [menuPlacement, setMenuPlacement] = useState<MenuPlacement>('bottom');
    const selectContainerRef = useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();
    const tryAction = useMultiselectTry();
    const {scale} = usePresScale();
    const presentation = useSelector(loadedPresentationSelector);
    const menuEstimatedHeightPx = getMenuEstimatedHeightPx(device, scale);

    const handleMenuOpen = useCallback((): void => {
        if (selectContainerRef.current) {
            setMenuPlacement(getMenuPlacementForSlide(selectContainerRef.current, menuEstimatedHeightPx));
        }
        setMenuIsOpen(true);
        setPlaceholder(openPlaceholder);
        dispatch(setPreventClickToNextSlide(true));
    }, [dispatch, openPlaceholder, menuEstimatedHeightPx]);

    const handleMenuClose = useCallback((): void => {
        setMenuIsOpen(false);
        setPlaceholder(closedPlaceholder);
        setTimeout(() => dispatch(setPreventClickToNextSlide(false)), 200);
    }, [dispatch, closedPlaceholder]);

    const {data: markedCodebookAnswers} = useQuery(
        ['markedCodebookEntries', element.id, elementInteractions.markedAnswersIds],
        () => elementInteractions.markedAnswersIds && fetchCodebookEntriesByIds(elementInteractions.markedAnswersIds),
        {
            enabled: !!codebookId && !!elementInteractions.markedAnswersIds && elementInteractions.markedAnswersIds.length > 0,
            select: (data) => (data ? mapCodebookEntriesToAnswers(element, data) : []),
        }
    );

    const {data: clickedCodebookAnswers} = useQuery(
        ['clickedCodebookEntries', element.id, elementInteractions.clickedAnswersIds],
        () => elementInteractions.clickedAnswersIds && fetchCodebookEntriesByIds(elementInteractions.clickedAnswersIds),
        {
            enabled: !!codebookId && !!elementInteractions.clickedAnswersIds && elementInteractions.clickedAnswersIds.length > 0,
            select: (data) => (data ? mapCodebookEntriesToAnswers(element, data) : []),
        }
    );

    const markedAnswers = codebookId
        ? markedCodebookAnswers || []
        : elementInteractions.markedAnswers?.map((c) => element.answers[c]) || [];

    const confirmedAnswers = codebookId
        ? clickedCodebookAnswers || []
        : elementInteractions.clickedAnswers?.map((c) => element.answers[c]) || [];

    const isAnswered = elementInteractions.answered;

    const [searchAnswers, setSearchAnswers] = useState<string>('');
    const {data, fetchNextPage, hasNextPage, isFetchingNextPage} = useInfiniteQuery(
        ['codebookEntries', codebookId, searchAnswers],
        ({pageParam = 1}) => (codebookId ? fetchCodebookEntries(codebookId, pageParam, 20, searchAnswers) : undefined),
        {
            enabled: !!codebookId,
            getNextPageParam: (lastPage) => {
                return getNextPage(lastPage);
            },
        }
    );

    const codebookAnswers = data?.pages.flatMap((page) => mapCodebookEntriesToAnswers(element, page?.content || [])) ?? [];
    const answers = codebookId ? codebookAnswers : element.answers;

    const handleScroll = (): void => {
        if (codebookId && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    function handleSingleSelectChange(e: SingleValue<IOptions>): number[] | string[] {
        if (codebookId) {
            return e?.value ? [e.value] : [];
        }
        const answeredIndexes = element.answers.findIndex((answer) => answer.text === e?.value);
        return answeredIndexes !== -1 ? [answeredIndexes] : [];
    }

    function handleMultiSelectChange(e: MultiValue<IOptions>): number[] | string[] {
        if (codebookId) {
            const answeredIds = e.map((item) => item.value);
            return getSymmetricalDifference(answeredIds, elementInteractions?.markedAnswersIds || []);
        }
        const answeredIndexes = e.map((item) => element.answers.findIndex((answer) => answer.text === item.value)).filter((e) => e !== -1);
        return getSymmetricalDifference(answeredIndexes, elementInteractions?.markedAnswers || []);
    }

    function handleSelectChange(e: MultiValue<IOptions> | SingleValue<IOptions>): void {
        if (isAnswered) return;

        let clicked;
        if (element.singleCorrect) {
            clicked = handleSingleSelectChange(e as SingleValue<IOptions>);
        } else {
            clicked = handleMultiSelectChange(e as MultiValue<IOptions>);
        }

        if (clicked.length > 0)
            handleSelectAnswer(
                elementInteractions,
                element,
                clicked[0],
                (codebookId ? elementInteractions?.markedAnswersIds : elementInteractions?.markedAnswers) || [],
                savePresentationRunFile,
                presentation
            );
    }

    function mapOptions(answers: IElementQuestionAnswer[]): IOptions[] {
        return answers.map((answer) => ({
            label: answer.text,
            value: answer.codebookEntryId || answer.text,
            type: answer?.type || ('incorrect' as IQuestionAnswerType),
            comment: answer?.comment || '',
            depth: answer.depth || 0,
            numberOfChildren: answer.numberOfChildren || 0,
        }));
    }

    function getSingleValue(): IOptions | undefined {
        return isAnswered ? mapOptions(markedAnswers)[0] : undefined;
    }

    return (
        <QuestionLayout
            element={element}
            elementInteractions={elementInteractions}
            device={device}
            showConfirmButton
            onConfirm={() => tryAction(element, elementInteractions, markedAnswers)}
            confirmDisabled={isAnswered || markedAnswers.length === 0}
        >
            <div className={`c-multiSelectElement ${device === DeviceType.MOBILE && 'c-multiSelectElement--mobile'}`}>
                <div ref={selectContainerRef} className="c-multiSelectElement__select">
                    <ReSelect
                        //  eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        selectProps={
                            {
                                answered: isAnswered,
                                correctColor: element.correctColor,
                                incorrectColor: element.incorrectColor,
                                debatableColor: element.debatableColor,
                                singleCorrect: element.singleCorrect,
                                device,
                                onCommentOpen: (answer: string, comment: string) => setCommentSheet({answer, comment}),
                            } as MultiQuestionSelectProps
                        }
                        options={mapOptions(answers)}
                        value={mapOptions(isAnswered ? confirmedAnswers : markedAnswers)}
                        {...(element.singleCorrect ? {defaultValue: getSingleValue()} : {isMulti: true})}
                        onChange={handleSelectChange}
                        onInputChange={(value) => setSearchAnswers(value)}
                        onMenuScrollToBottom={() => handleScroll()}
                        placeholder={placeholder}
                        hideSelectedOptions={false}
                        closeMenuOnSelect={!!element.singleCorrect}
                        blurInputOnSelect={!!element.singleCorrect}
                        menuIsOpen={menuIsOpen}
                        onMenuOpen={handleMenuOpen}
                        onMenuClose={handleMenuClose}
                        menuPlacement={menuPlacement}
                        menuPosition="fixed"
                        menuShouldScrollIntoView={false}
                        components={{
                            Option: MultiSelectOption,
                            ValueContainer: MultiSelectValueContainer,
                        }}
                        styles={{
                            multiValue: () => ({
                                display: 'none',
                            }),
                            singleValue: () => ({
                                display: 'none',
                            }),
                            indicatorSeparator: () => ({display: 'none'}),
                            indicatorsContainer: (base) => ({...base, display: 'none', padding: '.5em'}),
                            dropdownIndicator: (base) => ({...base, padding: '.5em'}),
                            valueContainer: (base) => ({
                                ...base,
                                padding: '0.125em 2.875em 0.125em .5em',
                                display: 'flex',
                            }),
                            placeholder: (base) => ({
                                ...base,
                                margin: '0 0.125em',
                                fontSize: '0.875em',
                                fontFamily: element.fontFamily?.name,
                            }),
                            input: (base) => ({...base, padding: '0.125em 0', margin: '0.125em'}),
                            control: (base) => ({
                                ...base,
                                flexWrap: 'wrap',
                                fontSize: `${device === DeviceType.DESKTOP ? element.fontSize : 1}em`,
                                borderRadius: '0.5em',
                                borderWidth: '0.0625em',
                                minHeight: '2.375em',
                                cursor: 'pointer',
                                backgroundColor: '#f4f5f6',
                            }),
                            option: (base) => ({
                                ...base,
                                fontSize: `${device === DeviceType.DESKTOP ? element.fontSize : 1}em`,
                                padding: '.2em .75em',
                                margin: '0',
                                backgroundColor: 'white !important',
                                color: '#676666',
                                cursor: 'pointer',
                                fontFamily: element.fontFamily?.name,
                            }),
                            container: (base) => ({...base, width: '100%'}),
                            menu: (provided) => ({
                                ...provided,
                                fontSize: `${getMenuFontSizePx(device, scale)}px`,
                                padding: '.75em 0',
                                borderRadius: '0.5em',
                            }),
                            menuList: (provided) => ({
                                ...provided,
                                fontSize: `${getMenuFontSizePx(device, scale)}px`,
                                maxHeight: `${MENU_LIST_MAX_HEIGHT_EM}em`,
                                // comment badge and its invisible hover hit-area intentionally peeks past its answer row's edge
                                overflowX: 'hidden',
                            }),
                        }}
                        menuPortalTarget={document.body}
                    />
                </div>
            </div>
            {device === DeviceType.MOBILE && (
                <BottomSheet
                    open={!!commentSheet}
                    onDismiss={() => setCommentSheet(null)}
                    title={commentSheet?.answer ?? ''}
                    content={commentSheet?.comment ?? ''}
                />
            )}
        </QuestionLayout>
    );
}
